/**
 * Web Vitals Performance Monitoring
 * Tracks Core Web Vitals (LCP, INP, CLS, FCP, TTFB) and reports to analytics.
 */

type Metric = {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
};

type ReportHandler = (metric: Metric) => void;

/**
 * Reports web vitals to Google Analytics 4
 */
const sendToAnalytics = (metric: Metric) => {
  // Send to Google Analytics if available
  if (typeof window !== 'undefined' && 'gtag' in window) {
    const gtag = (window as any).gtag;
    gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: metric.id,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
      metric_rating: metric.rating,
    });
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
  }
};

/**
 * Initialize Web Vitals reporting
 * Call this in your main entry file
 */
export async function reportWebVitals(onReport: ReportHandler = sendToAnalytics) {
  if (typeof window === 'undefined') return;

  try {
    // Dynamic import - package is optional, fails gracefully if not installed
    const webVitals = await import(/* @vite-ignore */ 'web-vitals').catch(() => null);
    
    if (!webVitals) {
      if (import.meta.env.DEV) {
        console.info('[Web Vitals] Package not installed. Run: npm install web-vitals');
      }
      return;
    }
    
    const { onCLS, onFCP, onLCP, onTTFB, onINP } = webVitals;

    // Cumulative Layout Shift (target: < 0.1)
    onCLS(onReport);

    // Interaction to Next Paint (target: < 200ms)
    onINP(onReport);

    // First Contentful Paint (target: < 1.8s)
    onFCP(onReport);

    // Largest Contentful Paint (target: < 2.5s)
    onLCP(onReport);

    // Time to First Byte (target: < 800ms)
    onTTFB(onReport);
  } catch (error) {
    // web-vitals library not installed or failed to load
    if (import.meta.env.DEV) {
      console.warn('[Web Vitals] Failed to load:', error);
    }
  }
}

/**
 * Performance thresholds for Core Web Vitals
 */
export const VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
} as const;

/**
 * Get performance rating based on value
 */
export function getPerformanceRating(
  metric: keyof typeof VITALS_THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = VITALS_THRESHOLDS[metric];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

export default reportWebVitals;
